import { defineStore } from 'pinia'
import { computed, ref, Ref } from 'vue'
import { useUserStore } from './user'
import { DEBUG, LocalizationApi } from '@/util'

export interface LocalizationDTO {
    id: string,
    text: string
}

/** Store of localized texts */
export const useLocalizationStore = defineStore('localizationStore', () => {
    /********************************************************************************************************************
     STATE
     ********************************************************************************************************************/
    const _language: Ref<string> = ref('');
    const _data: Ref<null | Map<string, string>> = ref(null);
    const _hasLoaded: Ref<boolean> = ref(false);

    /********************************************************************************************************************
     GETTERS
    ********************************************************************************************************************/
    /** currently active language */
    const language = computed(() => _language.value);

    /** whether the initial localization data has been loaded */
    const hasLoaded = computed(() => _hasLoaded.value);

    /********************************************************************************************************************
     ACTIONS
     ********************************************************************************************************************/
    /** Sets the currently active language and automatically reloads data */
    function setLanguage(language: string) {
        if (language != _language.value) {
            _language.value = language;
            loadTexts();
        }
    }

    /** Forces reload of the data of the currently set language */
    function loadTexts() {
        DEBUG('LocalizationStore: Fetching localization data...');
        const api = new LocalizationApi
        api.get(_language.value)
            .then(response => {
                _data.value = new Map<string, string>(
                    response.data.map(t => [t.id.toLowerCase(), t.text])
                )
            })
            .catch(error => DEBUG('LocalizationStore: Failed to fetch localization data:', error))
            .finally(() => _hasLoaded.value = true);
    }

    /** Returns the localized string related to the specified id 
        @param id: Id of the localized string
        */
    function get(id: string) {
        return _data.value?.get(id?.toLowerCase()) ?? `[${id}]`;
    }

    /********************************************************************************************************************
     MISC
     ********************************************************************************************************************/
    // Setup dependency on user store
    const userStore = useUserStore();

    userStore.$subscribe((mutation, state) => {
        setLanguage(state._language)
    });

    if (!language.value) {
        setLanguage(userStore.language);
    }

    return { _language, _data, _hasLoaded, language, hasLoaded, setLanguage, loadTexts, get }
})